<template>
    <div>
        <div v-if="loading">
            <loading></loading>
        </div>

        <div v-else>
            <b-alert variant="danger" :show="hasErrors">
                <span v-html="errorMessage"></span>
            </b-alert>
            <b-table
                responsive
                small
                :items="filteredItems"
                :fields="fields"
                :filter="filter"
                :filter-included-fields="['search']"
                :per-page="perPage"
                :current-page="currentPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                primary-key="client_id"
                @filtered="onFiltered"
            >
                <template #cell(month_billed)="data">
                    <span class="number-font">{{ formatNumber(data.item.month_billed) }}</span>
                </template>
                <template #cell(month_real)="data">
                    <span class="number-font">{{ formatNumber(data.item.month_real) }}</span>
                </template>
                <template #cell(month_margin)="data">
                    <span class="number-font">{{ formatNumber(data.item.month_margin) }}</span>
                </template>
                <template #cell(year_billed)="data">
                    <span class="number-font">{{ formatNumber(data.item.year_billed) }}</span>
                </template>
                <template #cell(year_real)="data">
                    <span class="number-font">{{ formatNumber(data.item.year_real) }}</span>
                </template>
                <template #cell(year_margin)="data">
                    <span class="number-font">{{ formatNumber(data.item.year_margin) }}</span>
                </template>

                <!-- FOOT -->
                <template #custom-foot="data">
                    <b-tr class="bg-light">
                        <b-th>TOTAL</b-th>
                        <b-th class="text-right number-font">{{
                            formatNumber(total.month_billed)
                        }}</b-th>
                        <b-th class="text-right number-font">{{
                            formatNumber(total.month_real)
                        }}</b-th>
                        <b-th class="text-right number-font my-vertical-border">{{
                            formatNumber(total.month_margin)
                        }}</b-th>
                        <b-th class="text-right number-font">{{
                            formatNumber(total.year_billed)
                        }}</b-th>
                        <b-th class="text-right number-font">{{
                            formatNumber(total.year_real)
                        }}</b-th>
                        <b-th class="text-right number-font">{{
                            formatNumber(total.year_margin)
                        }}</b-th>
                    </b-tr>
                </template>
            </b-table>
            <!-- Pagination -->
            <b-row>
                <b-col md="3" class="my-1 text-center">
                    <b-pagination
                        v-if="totalRows > 0"
                        v-model="currentPage"
                        :total-rows="totalRows"
                        :per-page="perPage"
                        class="my-0"
                        size="sm"
                    ></b-pagination>
                </b-col>
                <b-col md="6" class="my-2">
                    <p class="text-center small pt-0">{{ legend }}</p>
                </b-col>
                <b-col md="3" class="my-1 text-right">
                    <b-dropdown
                        right
                        split
                        size="sm"
                        :text="perPage + ' éléments par page'"
                    >
                        <b-dropdown-item
                            v-for="pageOption in pageOptions"
                            :key="pageOption"
                            @click="setPerPage(pageOption)"
                        >
                            {{ pageOption }}
                        </b-dropdown-item>
                    </b-dropdown>
                </b-col>
            </b-row>
            <!-- /Pagination -->
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import loading from '../../../common/components/loading'
import TableHandler from '../../../common/mixins/TableHandler'
import DashboardHandler from '../../mixins/DashboardHandler'

export default {
    name: 'BilledVsRealFilterable',

    components: {
        loading,
    },

    mixins: [TableHandler, DashboardHandler],

    data() {
        return {
            fields: [
                { key: 'title', label: 'Client', sortable: true },
                {
                    key: 'month_billed',
                    label: 'Facturé M',
                    class: 'text-right td-dashboard-data',
                    sortable: true,
                },
                {
                    key: 'month_real',
                    label: 'Coût M',
                    class: 'text-right td-dashboard-data',
                    sortable: true,
                },
                {
                    key: 'month_margin',
                    label: 'Marge M',
                    class: 'text-right td-dashboard-data my-vertical-border',
                    sortable: true,
                },
                {
                    key: 'year_billed',
                    label: 'Facturé C',
                    class: 'text-right td-dashboard-data',
                    sortable: true,
                },
                {
                    key: 'year_real',
                    label: 'Coût C',
                    class: 'text-right td-dashboard-data',
                    sortable: true,
                },
                {
                    key: 'year_margin',
                    label: 'Marge C',
                    class: 'text-right td-dashboard-data',
                    sortable: true,
                },
            ],
            total: {},
            dataUrl: '',
            showByClient: true,
            showByActivity: true,
            showByCompany: true,
            filterType: 'all',
            filterId: '',
            pageTitle: 'Global',
            cellClasses: [],
            total_month_evolution: '?',
            total_year_evolution: '?',
        }
    },

    async created() {
        this.dataUrl = 'dashboard/billed-vs-real/filterable/'
        this.$store.commit(
            'browsing/SET_PAGE_NAME',
            'billed-vs-real-filterable'
        )
        this.resetSearch()
        await this.getData()
    },

    methods: {
        initTotal() {
            this.total.month_billed = 0
            this.total.month_real = 0
            this.total.month_margin = 0
            this.total.year_billed = 0
            this.total.year_real = 0
            this.total.year_margin = 0
        },

        async getData() {
            this.loading = true
            try {
                const response = await axios.get(
                    `${this.dataUrl + this.year}/${this.month}`
                )
                this.items = response.data.data
                this.calculateTotal()
                this.updateTotalRows()
                this.loading = false
            } catch (error) {
                console.log(error)
                this.handleErrors(error.response)
            }
        },

        calculateTotal() {
            this.initTotal()

            this.items.forEach(
                (item) => (this.total.month_billed += item.month_billed)
            )
            this.items.forEach(
                (item) => (this.total.month_real += item.month_real)
            )
            this.items.forEach(
                (item) => (this.total.month_margin += item.month_margin)
            )
            this.items.forEach(
                (item) => (this.total.year_billed += item.year_billed)
            )
            this.items.forEach(
                (item) => (this.total.year_real += item.year_real)
            )
            this.items.forEach(
                (item) => (this.total.year_margin += item.year_margin)
            )

            if (this.total.month_real === 0) {
                this.total_month_evolution = '-'
            } else {
                const evolution =
                    (this.total.month_billed - this.total.month_real) /
                    this.total.month_real
                this.total_month_evolution = `${Math.floor(
                    100 * evolution
                ).toLocaleString()}%`
            }

            if (this.total.year_real === 0) {
                this.total_year_evolution = '-'
            } else {
                const evolution =
                    (this.total.year_billed - this.total.year_real) /
                    this.total.year_real
                this.total_year_evolution = `${Math.floor(
                    100 * evolution
                ).toLocaleString()}%`
            }
        },

        formatNumber(number) {
            if (number === 0) {
                return '-'
            }
            if (number === null) {
                return '-'
            }
            return Math.floor(number).toLocaleString()
        },
    },

    computed: {
        ...mapState({
            year: (state) => state.browsing.year,
            month: (state) => state.browsing.month,
        }),
    },
}
</script>

<style scoped></style>
